// Time-based tweens driven by the stage's frame loop.

export type Ease = (t: number) => number;

export const ease = {
  linear: (t: number) => t,
  inOut: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  out: (t: number) => 1 - Math.pow(1 - t, 3),
  in: (t: number) => t * t * t,
  backOut: (t: number) => {
    const c = 1.70158;
    return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2);
  },
} satisfies Record<string, Ease>;

interface Item {
  elapsed: number;
  duration: number;
  step: (t: number) => void;
  ease: Ease;
  resolve: () => void;
  key?: object | string;
}

export class Tweens {
  private items = new Set<Item>();

  /** Run `step` with an eased 0..1 over `duration` ms. A `key` replaces any tween sharing it. */
  run(duration: number, step: (t: number) => void, easing: Ease = ease.inOut, key?: object | string): Promise<void> {
    if (key !== undefined) this.cancel(key);
    return new Promise((resolve) => {
      if (duration <= 0) {
        step(1);
        resolve();
        return;
      }
      this.items.add({ elapsed: 0, duration, step, ease: easing, resolve, key });
    });
  }

  cancel(key: object | string): void {
    for (const item of this.items) {
      if (item.key === key) {
        this.items.delete(item);
        item.resolve();
      }
    }
  }

  /** Advance every tween; true while any is still running. */
  update(dt: number): boolean {
    for (const item of [...this.items]) {
      item.elapsed += dt;
      const t = Math.min(1, item.elapsed / item.duration);
      item.step(item.ease(t));
      if (t >= 1) {
        this.items.delete(item);
        item.resolve();
      }
    }
    return this.items.size > 0;
  }
}
